import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DIALOGS from 'app/constants/dialogs';
import { openDialog, closeDialog, selectOpenedDialog, selectDialogOptions } from 'app/store/fuse/dialogSlice';
import useShowMessage from 'app/hooks/useShowMessage';
import { MESSAGE_TYPE } from 'app/constants/message';
import { injectReducer } from 'app/store/index';
import { authSelectors } from 'app/auth/store/userSlice';
import { useBillingPermissions } from '../hooks/useBillingPermissions';
import { TRANSLATION_KEY, SUBSCRIPTION_STATUS, PATHS } from '../constants';
import { adaptSubscription, formatPrice, formatPeriod, isTrialExpired } from '../utils';
import billingReducer from '../store';
import { actions, selectors, STATUS } from '../store/billingSlice';
import UpgradePlanDialog from './UpgradePlanDialog';
import UpgradeConfirmDialog from './UpgradeConfirmDialog';
import DowngradeConfirmDialog from './DowngradeConfirmDialog';
import BillingDetailsDialog from './BillingDetailsDialog';
import PaymentDialog from './PaymentDialog';
import RequestScalePlanDialog from './RequestScalePlanDialog';
import RefillBalanceDialog from './RefillBalanceDialog';
import CancelSubscriptionDialog from './CancelSubscriptionDialog';
import RenewSubscriptionDialog from './RenewSubscriptionDialog';
import TrialEndedDialog from './TrialEndedDialog';

injectReducer('billing', billingReducer);

// Subscription is re-read on navigation, but no more often than this
const SUBSCRIPTION_REFETCH_INTERVAL = 60_000;

const UPGRADE_DIALOGS = new Set([
  DIALOGS.UPGRADE_PLAN,
  DIALOGS.UPGRADE_CONFIRM,
  DIALOGS.DOWNGRADE_CONFIRM,
  DIALOGS.BILLING_DETAILS,
  DIALOGS.PAYMENT,
  DIALOGS.REQUEST_SCALE_PLAN,
]);

const GlobalBillingDialogs = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(TRANSLATION_KEY);
  const showMessage = useShowMessage();
  const openedDialog = useSelector(selectOpenedDialog);
  const apiSubscription = useSelector(selectors.selectCurrentSubscription);
  const serverTime = useSelector(selectors.selectServerTime);
  const tariffs = useSelector(selectors.selectTariffs);
  const tariffMeta = useSelector(selectors.selectTariffMeta);
  const billingProfile = useSelector(selectors.selectBillingProfile);
  const authCurrency = useSelector(authSelectors.selectPreferredCurrency);
  const currency = billingProfile?.preferredCurrency ?? authCurrency;
  const paymentData = useSelector(selectors.selectPaymentData);
  const dialogOptions = useSelector(selectDialogOptions);
  const upgradeStatus = useSelector(selectors.selectUpgradeSubscriptionStatus);
  const downgradeStatus = useSelector(selectors.selectDowngradeSubscriptionStatus);
  const cancelStatus = useSelector(selectors.selectCancelSubscriptionStatus);
  const renewStatus = useSelector(selectors.selectRenewSubscriptionStatus);
  const [dialogDataLoaded, setDialogDataLoaded] = useState(false);
  const location = useLocation();
  const lastSubscriptionFetchRef = useRef(0);
  const trialEndedDialogShownRef = useRef(false);
  const {
    canViewSubscription,
    canViewBillingProfile,
    canDeposit,
    canCreateSubscription,
    canUpgradeSubscription,
  } = useBillingPermissions();

  // Fetch subscription globally on mount and on every navigation, throttled
  useEffect(() => {
    if (!canViewSubscription) return;

    const now = Date.now();
    if (now - lastSubscriptionFetchRef.current < SUBSCRIPTION_REFETCH_INTERVAL) return;

    lastSubscriptionFetchRef.current = now;
    dispatch(actions.getCurrentSubscription());
  }, [dispatch, canViewSubscription, location.pathname]);

  // Fetch tariffs & billing profile lazily when a dialog opens
  useEffect(() => {
    if (UPGRADE_DIALOGS.has(openedDialog) && !dialogDataLoaded) {
      dispatch(actions.getTariffs());
      if (canViewBillingProfile) {
        dispatch(actions.getBillingProfile());
      }
      setDialogDataLoaded(true);
    }
  }, [openedDialog, dialogDataLoaded, dispatch, canViewBillingProfile]);

  // Announce an expired trial once per page load; dismissing it must not bring it back
  useEffect(() => {
    if (trialEndedDialogShownRef.current) return;
    if (!canCreateSubscription && !canUpgradeSubscription) return;
    // Never steal the slot from a dialog the user already has open (dialogSlice holds one at a time)
    if (openedDialog) return;
    // On the billing page the same message is already on screen
    if (location.pathname.startsWith(PATHS.BILLING)) return;
    if (!isTrialExpired(apiSubscription, serverTime)) return;

    trialEndedDialogShownRef.current = true;
    dispatch(openDialog({ dialogId: DIALOGS.TRIAL_ENDED }));
  }, [
    dispatch,
    apiSubscription,
    serverTime,
    openedDialog,
    location.pathname,
    canCreateSubscription,
    canUpgradeSubscription,
  ]);

  const subscription = apiSubscription ? adaptSubscription(apiSubscription, serverTime, currency) : null;

  // Compute new plan details for upgrade/downgrade dialogs from dialogOptions + tariffs
  const newPlanForDialog = useMemo(() => {
    if (openedDialog !== DIALOGS.UPGRADE_CONFIRM && openedDialog !== DIALOGS.DOWNGRADE_CONFIRM) return null;
    const { planId, variantId } = dialogOptions || {};
    const tariff = tariffs.find((item) => item.id === planId);
    const variant = tariff?.variants?.find((v) => v.id === variantId);
    return tariff
      ? {
          tariffPlanId: planId,
          tariffPlanVariantId: variantId,
          name: tariff.name,
          price: formatPrice(variant, currency),
          period: formatPeriod(variant),
        }
      : null;
  }, [openedDialog, dialogOptions, tariffs, currency]);

  const openPaymentDialog = useCallback(() => {
    dispatch(openDialog({ dialogId: DIALOGS.PAYMENT }));
  }, [dispatch]);

  const proceedToSubscription = useCallback(
    (planId, variantId) => {
      dispatch(actions.createSubscription({ tariffPlanId: planId, tariffPlanVariantId: variantId }))
        .unwrap()
        .then(openPaymentDialog)
        .catch((error) => {
          showMessage(error?.message || String(error), MESSAGE_TYPE.ERROR);
        });
    },
    [dispatch, showMessage, openPaymentDialog]
  );

  const handleSelectPlan = useCallback(
    (planId, variantId) => {
      dispatch(closeDialog());

      if (subscription?.status === SUBSCRIPTION_STATUS.ACTIVE) {
        // Compare prices to decide upgrade vs downgrade
        const newTariff = tariffs.find((item) => item.id === planId);
        const newVariant = newTariff?.variants?.find((v) => v.id === variantId);
        const newAmount = parseFloat(newVariant?.amount ?? 0);
        const currentAmount = parseFloat(subscription.priceAmount ?? 0);

        if (newAmount < currentAmount) {
          dispatch(openDialog({ dialogId: DIALOGS.DOWNGRADE_CONFIRM, planId, variantId }));
        } else {
          dispatch(openDialog({ dialogId: DIALOGS.UPGRADE_CONFIRM, planId, variantId }));
        }
      } else if (billingProfile) {
        proceedToSubscription(planId, variantId);
      } else {
        dispatch(openDialog({ dialogId: DIALOGS.BILLING_DETAILS, planId, variantId }));
      }
    },
    [dispatch, subscription, tariffs, billingProfile, proceedToSubscription]
  );

  const handleUpgradeConfirm = useCallback(
    (planId, variantId) => {
      dispatch(actions.upgradeSubscription({ tariffPlanId: planId, tariffPlanVariantId: variantId }))
        .unwrap()
        .then(openPaymentDialog)
        .catch((error) => {
          showMessage(error?.message || String(error), MESSAGE_TYPE.ERROR);
        });
    },
    [dispatch, showMessage, openPaymentDialog]
  );

  const handleDowngradeConfirm = useCallback(
    (planId, variantId) => {
      dispatch(actions.downgradeSubscription({ tariffPlanId: planId, tariffPlanVariantId: variantId }))
        .unwrap()
        .then(() => {
          dispatch(closeDialog());
          dispatch(actions.getCurrentSubscription());
          showMessage(t('DOWNGRADE_SUCCESS'), MESSAGE_TYPE.SUCCESS);
        })
        .catch((error) => {
          showMessage(error?.message || String(error), MESSAGE_TYPE.ERROR);
        });
    },
    [dispatch, showMessage, t]
  );

  const handleRefill = useCallback(
    (amount) => {
      dispatch(actions.deposit({ amount }))
        .unwrap()
        .then(openPaymentDialog)
        .catch((error) => {
          showMessage(error?.message || String(error), MESSAGE_TYPE.ERROR);
        });
    },
    [dispatch, showMessage, openPaymentDialog]
  );

  const handleLetsTalk = useCallback(() => {
    dispatch(openDialog({ dialogId: DIALOGS.REQUEST_SCALE_PLAN }));
  }, [dispatch]);

  const handlePaymentComplete = useCallback(() => {
    dispatch(actions.getCurrentSubscription());
    dispatch(actions.getInvoices());
    if (canDeposit) {
      dispatch(actions.getPaymentCard());
    }
  }, [dispatch, canDeposit]);

  const handlePaymentFailed = useCallback(
    (data) => {
      dispatch(actions.clearPaymentData());
      showMessage(data?.message || t('PAYMENT_FAILED'), MESSAGE_TYPE.ERROR);
    },
    [dispatch, showMessage, t]
  );

  return (
    <>
      <UpgradePlanDialog
        tariffs={tariffs}
        tariffMeta={tariffMeta}
        currency={currency}
        currentPlanId={subscription?.planId}
        currentVariantId={subscription?.variantId}
        subscriptionStatus={subscription?.status}
        onSelectPlan={handleSelectPlan}
        onLetsTalk={handleLetsTalk}
      />
      <UpgradeConfirmDialog
        currentPlan={{
          name: subscription?.planName,
          price: subscription?.price,
          period: subscription?.period,
        }}
        newPlan={newPlanForDialog}
        renewsOn={subscription?.renewalInfo}
        isLoading={upgradeStatus === STATUS.PENDING}
        onConfirm={handleUpgradeConfirm}
      />
      <DowngradeConfirmDialog
        currentPlan={{
          name: subscription?.planName,
          price: subscription?.price,
          period: subscription?.period,
        }}
        newPlan={newPlanForDialog}
        renewsOn={subscription?.renewalInfo}
        isLoading={downgradeStatus === STATUS.PENDING}
        onConfirm={handleDowngradeConfirm}
      />
      <BillingDetailsDialog
        onSuccess={() => {
          if (dialogOptions?.planId && dialogOptions?.variantId) {
            proceedToSubscription(dialogOptions.planId, dialogOptions.variantId);
          } else if (dialogOptions?.next) {
            dispatch(openDialog({ dialogId: dialogOptions.next }));
          }
        }}
      />
      <PaymentDialog
        paymentUrl={paymentData?.paymentUrl}
        onPaymentComplete={handlePaymentComplete}
        onPaymentFailed={handlePaymentFailed}
        onClose={() => dispatch(actions.clearPaymentData())}
      />
      <RequestScalePlanDialog />
      <RefillBalanceDialog currency={currency} onConfirm={handleRefill} />
      <CancelSubscriptionDialog
        isLoading={cancelStatus === STATUS.PENDING}
        onConfirm={() => {
          dispatch(actions.cancelSubscription({ subscriptionId: apiSubscription?.id }))
            .unwrap()
            .then(() => {
              dispatch(closeDialog());
              dispatch(actions.getCurrentSubscription());
              const endDate = apiSubscription?.endDate
                ? format(new Date(apiSubscription.endDate), 'dd MMM yyyy')
                : null;
              showMessage(t('CANCEL_SUBSCRIPTION_SUCCESS', { date: endDate }), MESSAGE_TYPE.SUCCESS);
            })
            .catch((error) => {
              showMessage(error?.message || String(error), MESSAGE_TYPE.ERROR);
            });
        }}
      />
      <TrialEndedDialog />
      <RenewSubscriptionDialog
        isLoading={renewStatus === STATUS.PENDING}
        onConfirm={() => {
          dispatch(actions.renewSubscription({ subscriptionId: apiSubscription?.id }))
            .unwrap()
            .then(() => {
              dispatch(closeDialog());
              dispatch(actions.getCurrentSubscription());
              showMessage(t('RENEW_SUBSCRIPTION_SUCCESS'), MESSAGE_TYPE.SUCCESS);
            })
            .catch((error) => {
              showMessage(error?.message || String(error), MESSAGE_TYPE.ERROR);
            });
        }}
      />
    </>
  );
};

export default GlobalBillingDialogs;
